// components/Hero.jsx — FRESH DESIGN ✅
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function useCounter(end, dur = 2000) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let s = 0;
    const step = end / (dur / 16);
    const t = setInterval(() => {
      s += step;
      if (s >= end) { setN(end); clearInterval(t); }
      else setN(Math.floor(s));
    }, 16);
    return () => clearInterval(t);
  }, [end, dur]);
  return n;
}

function useTypewriter(words) {
  const [txt, setTxt] = useState('');
  const [wi, setWi] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    const w = words[wi];
    const t = setTimeout(() => {
      if (!del) {
        setTxt(w.substring(0, txt.length + 1));
        if (txt === w) { setTimeout(() => setDel(true), 2200); return; }
      } else {
        setTxt(w.substring(0, txt.length - 1));
        if (txt === '') { setDel(false); setWi(p => (p + 1) % words.length); }
      }
    }, del ? 40 : 80);
    return () => clearTimeout(t);
  }, [txt, del, wi, words]);
  return txt;
}

export default function Hero({ onOpenEnquiry }) {
  const navigate = useNavigate();
  const campaigns = useCounter(500);
  const roas = useCounter(32);
  const clients = useCounter(150);
  const typed = useTypewriter(['Performance Marketing', 'Revenue Growth', 'Multi-Platform Ads', 'Data-Driven Results']);

  return (
    <section className="relative bg-white min-h-[100dvh] flex flex-col overflow-hidden">

      {/* ── Background ── */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-[30%] -right-[15%] w-[60vw] max-w-[700px] aspect-square rounded-full bg-gradient-to-br from-[#00809D]/[0.07] to-[#00a0c4]/[0.03] blur-[60px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[40vw] max-w-[500px] aspect-square rounded-full bg-[#00809D]/[0.04] blur-[80px]" />
        <div className="absolute inset-0 opacity-[0.15]" style={{
          backgroundImage: 'linear-gradient(#00809D 0.5px, transparent 0.5px), linear-gradient(90deg, #00809D 0.5px, transparent 0.5px)',
          backgroundSize: '60px 60px'
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-transparent to-white" />
      </div>

      {/* ── Content (Centered Full-Width) ── */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
          <div className="flex flex-col items-center text-center space-y-7 sm:space-y-8">

            {/* Tag */}
            <div className="si" style={{ '--d': '0s' }}>
              <span className="inline-flex items-center gap-2 text-[#00809D] text-xs sm:text-sm font-bold uppercase tracking-[0.12em] bg-[#00809D]/[0.06] px-4 py-2 rounded-full border border-[#00809D]/10">
                #1 Performance Ad Agency
              </span>
            </div>

            {/* Headline */}
            <div className="si" style={{ '--d': '0.1s' }}>
              <h1 className="text-[2.25rem] sm:text-[3rem] md:text-[3.75rem] lg:text-[4.5rem] xl:text-[5rem] font-black text-gray-900 leading-[1.08] tracking-tight">
                We Build Ads That
                <br />
                <span className="relative inline-block text-[#00809D]">
                  Actually Convert
                  <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full h-2 sm:h-3" viewBox="0 0 200 10" preserveAspectRatio="none"><path d="M0 8 Q50 0, 100 5 Q150 10, 200 3" stroke="#00809D" strokeWidth="3" fill="none" opacity="0.3" strokeLinecap="round" className="underline-draw" /></svg>
                </span>
              </h1>
            </div>

            {/* Typed subtitle */}
            <div className="si" style={{ '--d': '0.2s' }}>
              <div className="flex items-center justify-center gap-2">
                <div className="w-1 h-6 sm:h-7 bg-[#00809D] rounded-full" />
                <p className="text-gray-500 text-base sm:text-lg lg:text-xl min-h-[1.5em]">
                  Specializing in <span className="text-[#00809D] font-semibold">{typed}</span><span className="text-[#00809D]/50 blink">|</span>
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="si" style={{ '--d': '0.25s' }}>
              <p className="text-gray-400 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
                We help businesses scale their advertising with data-driven strategies across 15+ platforms. From campaign setup to full-funnel optimization — we deliver measurable results.
              </p>
            </div>

            {/* CTAs */}
            <div className="si" style={{ '--d': '0.35s' }}>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button onClick={() => { onOpenEnquiry && onOpenEnquiry(); }}
                  className="group relative bg-gray-900 hover:bg-gray-800 text-white px-8 sm:px-10 py-4 rounded-2xl font-bold text-sm sm:text-base transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] shadow-xl shadow-gray-900/20 hover:shadow-2xl hover:shadow-gray-900/30 flex items-center justify-center gap-2.5 overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-[#00809D]/0 via-[#00809D]/20 to-[#00809D]/0 -translate-x-[200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                  Start Free Audit
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </button>
              </div>
            </div>

            {/* Stats bar */}
            <div className="si" style={{ '--d': '0.45s' }}>
              <div className="inline-flex items-center bg-gray-50 border border-gray-100 rounded-2xl p-1.5 sm:p-2">
                {[
                  { v: `${campaigns}+`, l: "Campaigns", c: "text-gray-900" },
                  { v: `${(roas / 10).toFixed(1)}x`, l: "ROAS", c: "text-[#00809D]" },
                  { v: `${clients}+`, l: "Clients", c: "text-[#00809D]" },
                ].map((s, i) => (
                  <div key={i} className="flex items-center">
                    <div className="px-4 sm:px-6 py-2.5 sm:py-3 text-center cursor-default hover:bg-white hover:shadow-sm rounded-xl transition-all">
                      <div className={`text-xl sm:text-2xl lg:text-3xl font-extrabold ${s.c} tracking-tight leading-none`}>{s.v}</div>
                      <div className="text-gray-400 text-[10px] sm:text-xs mt-1">{s.l}</div>
                    </div>
                    {i < 2 && <div className="w-px h-8 sm:h-10 bg-gray-200 mx-0.5" />}
                  </div>
                ))}
              </div>
            </div>

            {/* Trusted platforms */}
            <div className="si" style={{ '--d': '0.55s' }}>
              <div className="flex items-center gap-3 sm:gap-4 justify-center flex-wrap">
                <span className="text-gray-300 text-[10px] sm:text-xs font-semibold uppercase tracking-widest">Trusted by</span>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  {[
                    { n: "Google", c: "#4285F4", d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.07 5.07 0 0 1-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" },
                    { n: "Meta", c: "#0668E1", d: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" },
                    { n: "TikTok", c: "#000", d: "M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" },
                    { n: "LinkedIn", c: "#0A66C2", d: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
                    { n: "YouTube", c: "#FF0000", d: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" },
                  ].map((p, i) => (
                    <div key={i} title={p.n} onClick={onOpenEnquiry} className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center hover:scale-110 hover:shadow-md transition-all duration-300 cursor-pointer">
                      <svg className="w-4 h-4" fill={p.c} viewBox="0 0 24 24"><path d={p.d} /></svg>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ─── Live Dashboard — COMMENTED OUT ───
      <div className="lg:col-span-5 sr w-full" style={{ '--d': '0.15s' }}>
        <div className="relative max-w-[380px] mx-auto">
          <div className="absolute -inset-8 sm:-inset-12 border border-[#00809D]/[0.06] rounded-[3rem] hidden lg:block" />
          <div className="absolute -inset-16 sm:-inset-20 border border-[#00809D]/[0.03] rounded-[4rem] hidden lg:block" />
          <div className="relative bg-white rounded-2xl sm:rounded-3xl border border-gray-200 shadow-2xl shadow-gray-200/60 overflow-hidden">
            <div className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                </div>
                <span className="text-white font-bold text-xs sm:text-sm">Live Dashboard</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/20 px-2 py-0.5 rounded-full">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                <span className="text-white/90 text-[10px] font-semibold">Active</span>
              </div>
            </div>
            <div className="p-4 sm:p-5 space-y-4">
              ... Revenue card, Mini stats, Bar chart, Footer ...
            </div>
          </div>
          <div className="absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-4 bg-white rounded-xl sm:rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 px-3 sm:px-4 py-2 sm:py-2.5 flex items-center gap-2 float-badge">
            ROI Positive — Within 30 days
          </div>
          <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 bg-white rounded-xl sm:rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 px-3 sm:px-4 py-2 sm:py-2.5 flex items-center gap-2 float-badge-2">
            +127% — Avg growth
          </div>
        </div>
      </div>
      ─── END Live Dashboard ─── */}

      {/* Bottom border */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      <style>{`
        .si { opacity:0; transform:translateY(20px); animation: slideIn .7s cubic-bezier(.16,1,.3,1) forwards; animation-delay: var(--d); }
        @keyframes slideIn { to { opacity:1; transform:translateY(0); } }

        .blink { animation: blinkC .8s step-end infinite; }
        @keyframes blinkC { 50% { opacity:0; } }

        .underline-draw { stroke-dasharray:300; stroke-dashoffset:300; animation: drawL 1s ease-out .4s forwards; }
        @keyframes drawL { to { stroke-dashoffset:0; } }
      `}</style>
    </section>
  );
}
